import { gql, useMutation } from '@apollo/client';
import {
  UpsertLiveMutation,
  UpsertLiveMutationVariables,
  UpdateLiveDatesMutation,
  UpdateLiveDatesMutationVariables,
  UpsertLivePostMutation,
  UpsertLivePostMutationVariables,
} from '../../__generated__/queries-web';
import { CurrentFieldData } from '../../pages/LiveEdit/reducer';

const UPSERT_LIVE = gql`
  mutation UpsertLive(
    $where: LiveUniqueFilterInput!
    $create: LiveCreationInput!
    $update: LiveUpdateInput!
  ) {
    upsertLive(where: $where, create: $create, update: $update) {
      article {
        id
      }
      id
      endedAt
      posts(first: 200, orderBy: [publishedAt_DESC]) {
        id
        publishedAt
        title
        body
      }
    }
  }
`;

const UPDATE_LIVE_DATES = gql`
  mutation UpdateLiveDates($where: LiveUniqueFilterInput!, $endDate: DateTime) {
    updateLive(where: $where, data: { endedAt: $endDate }) {
      id
      endedAt
      posts(first: 200, orderBy: [publishedAt_DESC]) {
        id
        publishedAt
        title
        body
      }
    }
  }
`;

const UPSERT_LIVE_POST = gql`
  mutation UpsertLivePost(
    $create: LivePostCreationInput!
    $where: LivePostUniqueFilterInput!
    $update: LivePostUpdateInput!
  ) {
    upsertLivePost(create: $create, where: $where, update: $update) {
      id
      publishedAt
      title
      body
      live {
        id
        endedAt
      }
    }
  }
`;

interface UpsertProps {
  articleId: string;
  onCompleted: (data: UpsertLiveMutation | undefined) => void;
}

interface UpsertParams {
  articleId: string;
  closeLive: boolean;
}

interface LivePostUpsertProps {
  data: CurrentFieldData;
  articleId: string;
  id?: string;
}

export const useLiveUpsert = ({ articleId, onCompleted }: UpsertProps) =>
  useMutation<UpsertLiveMutation, UpsertLiveMutationVariables>(UPSERT_LIVE, {
    onCompleted,
    variables: {
      update: {},
      create: {
        article: {
          connect: { id: articleId },
        },
      },
      where: {
        article: { id: articleId },
      },
    },
  });

export const useLiveUpdateDates = ({ closeLive, articleId }: UpsertParams) =>
  useMutation<UpdateLiveDatesMutation, UpdateLiveDatesMutationVariables>(
    UPDATE_LIVE_DATES,
    {
      variables: {
        endDate: closeLive ? new Date().toISOString() : null,
        where: {
          id: articleId,
        },
      },
    },
  );

export const useLivePostUpsert = ({
  id,
  articleId,
  data,
}: LivePostUpsertProps) => {
  const livePostId = id || crypto.randomUUID();

  return useMutation<UpsertLivePostMutation, UpsertLivePostMutationVariables>(
    UPSERT_LIVE_POST,
    {
      variables: {
        create: {
          live: {
            connect: {
              article: {
                id: articleId,
              },
            },
          },
          ...data,
        },
        where: {
          id: livePostId,
        },
        update: {
          ...data,
        },
      },
    },
  );
};
